import { OgqlViewDetail } from '@opengov/ogql-client/records';
import { IOgqlViewDetail } from '@opengov/ogql-types';
import { EEditMode, enumActiveNavigationPanelFilter } from '../../../src/types/MultiDatasetTypes';
import {
  ColumnValueSegment,
  EGroupType,
  IColumnDataSource,
  IReportConfiguration,
  IReportDatasetLens,
  MultiHierarchyColumnDataSource
} from '@opengov/reporting-types';
import type { ColDef } from 'ag-grid-community';
import { Map } from 'immutable';
import { composeStateMachine } from '../../../module';
import { AG_GRID_CACHE_BUSTER } from '../../../src/components/table/agGridConsts';
import { ReportExplorerState } from '../records';
import { DataTableStatus, IPdfExportData, SettingsCardMode } from '../types';

export const reportExplorerInitialState = ReportExplorerState();

export const reportExplorerState = composeStateMachine('report-explorer', reportExplorerInitialState, (compose) => ({
  initialize: compose.payloadlessAction((state) => state.set('initialized', true)),

  setIsRefreshRowData: compose.action((state, status: boolean) =>
    state.set('isRefreshRowData', status).set('isRefreshRowKeepingCache', false)
  ),

  /**
   * Purges the grid's rows without clearing the pivot cache. Used when columns finish loading on a
   * report open, where the cache holds the rows the prefetch just fetched.
   */
  requestRowRefreshKeepingCache: compose.payloadlessAction((state) =>
    state.set('isRefreshRowData', true).set('isRefreshRowKeepingCache', true)
  ),

  setFetchingPdfExportData: compose.action((state, status: boolean) => state.set('fetchingPdfExportData', status)),

  setPdfExportData: compose.action((state, data: IPdfExportData) => state.set('pdfExportData', data)),

  setPdfExportTooMuchData: compose.action((state, flag: boolean) => state.set('pdfExportTooMuchData', flag)),

  updateDataTableStatus: compose.action((state, status: DataTableStatus) => state.set('dataTableStatus', status)),

  updatePaginationOffset: compose.action((state, offset: number) => state.set('paginationOffset', offset)),

  updateDataTableTotalRowCount: compose.action((state, total: number) => state.set('dataTableTotalRowCount', total)),

  updateReportTotalRowCount: compose.action((state, count: number) => state.set('reportTotalRowCount', count)),

  setDimensionalQueriesLoading: compose.action((state, dimensionalQueries) =>
    state.set(
      'dimensionalTableQueryStatuses',
      dimensionalQueries.map(() => DataTableStatus.Loading)
    )
  ),

  setDimensionalQueryUpdating: compose.action((state, queryIndex) =>
    state.setIn(['dimensionalTableQueryStatuses', queryIndex], DataTableStatus.Updating)
  ),

  setDimensionalQueryFinished: compose.action((state, queryIndex) =>
    state.setIn(['dimensionalTableQueryStatuses', queryIndex], DataTableStatus.Current)
  ),

  detailTableQueryFailed: compose.payloadlessAction((state) => {
    return state.set('dataTableStatus', DataTableStatus.Current);
  }),
  receivedReportView: compose.action((state, view: IOgqlViewDetail) => {
    return state.set('currentView', OgqlViewDetail(view));
  }),
  resetState: compose.payloadlessAction((state) => state.clear()),
  onEditCardModeChange: compose.action((state, mode: SettingsCardMode) => {
    return state.set('settingsAboutCardMode', mode);
  }),
  setAllowedActions: compose.action((state, allowedActions: Map<string, boolean>) => {
    return state.set('allowedActions', allowedActions);
  }),
  setIsLoadingReportDatasetLenses: compose.action((state, status: boolean) =>
    state.set('isLoadingReportDatasetLenses', status)
  ),
  setReportDatasetLenses: compose.action((state, payload: IReportDatasetLens[]) =>
    state.set('reportDatasetLenses', payload)
  ),
  addReportDatasetLens: compose.action((state, payload: IReportDatasetLens) => {
    const currentLenses = state.get('reportDatasetLenses', []) || [];
    if (currentLenses.some((lens) => lens.id === payload.id)) return state;
    return state.set('reportDatasetLenses', currentLenses.concat(payload));
  }),
  setDimensionalData: compose.action(
    (
      state,
      payload: {
        data: any[];
        columns: any[];
        reportConfiguration: IReportConfiguration;
      }
    ) =>
      state
        .set('agGridCacheBuster', state.get('agGridCacheBuster', AG_GRID_CACHE_BUSTER) + 1)
        .set('dimensionalGroupRowData', payload.data)
        .set('dimensionalReportConfiguration', payload.reportConfiguration)
        .set('dimensionalColumns', payload.columns)
  ),
  setagGridColumns: compose.action(
    (
      state,
      payload: { agGridColumns: any[]; pivotColumns: ColumnValueSegment[][]; pivotColumnGroupIds?: string[] | null }
    ) =>
      state
        .set('agGridCacheBuster', state.get('agGridCacheBuster', AG_GRID_CACHE_BUSTER) + 1)
        .set('agGridColumns', payload.agGridColumns)
        .set('pivotColumns', payload.pivotColumns)
        .set('pivotColumnGroupIds', payload.pivotColumnGroupIds ?? null)
  ),
  setEditModeColumnContentToAddColumn: compose.action((state, payload: EGroupType) =>
    state
      .set('selectedColumnGroupIndex', null)
      .set('selectedRowGroupIndex', null)
      .set('newRowGroupType', null)
      .set('activeNavigationPanelFilter', enumActiveNavigationPanelFilter.ROOT)
      .set('newColumnGroupType', payload)
      .set('editMode', EEditMode.ColNew)
      .set('editorDrawerOpen', true)
  ),
  setEditModeRowContentToAddRow: compose.action((state, payload: EGroupType) =>
    state
      .set('selectedColumnGroupIndex', null)
      .set('selectedRowGroupIndex', null)
      .set('newColumnGroupType', null)
      .set('activeNavigationPanelFilter', enumActiveNavigationPanelFilter.ROOT)
      .set('editMode', EEditMode.RowNew)
      .set('newRowGroupType', payload)
      .set('editorDrawerOpen', true)
  ),
  setSelectedRowGroupIndex: compose.action((state, payload: number) =>
    state.set('selectedRowGroupIndex', payload).set('selectedColumnGroupIndex', null).set('newColumnGroupType', null)
  ),

  switchToDefaultEditMode: compose.payloadlessAction((state) =>
    state
      .set('isEditModeDirty', false)
      .set('editMode', EEditMode.Default)
      .set('selectedColumnGroupIndex', null)
      .set('selectedColumnEditorLensId', null)
      .set('selectedRowGroupIndex', null)
      .set('activeNavigationPanelFilter', enumActiveNavigationPanelFilter.ROOT)
      .set('newColumnGroupType', null)
      .set('newRowGroupType', null)
  ),

  switchToViewMode: compose.payloadlessAction((state) =>
    state
      .set('isEditModeDirty', false)
      .set('editMode', null)
      .set('selectedColumnGroupIndex', null)
      .set('selectedColumnEditorLensId', null)
      .set('selectedRowGroupIndex', null)
      .set('activeNavigationPanelFilter', enumActiveNavigationPanelFilter.ROOT)
      .set('newColumnGroupType', null)
      .set('newRowGroupType', null)
  ),
  setagGridParentData: compose.action((state, payload: { agGridParentData: any[] }) =>
    state
      .set('agGridCacheBuster', state.get('agGridCacheBuster', AG_GRID_CACHE_BUSTER) + 1)
      .set('agGridParentData', payload.agGridParentData)
  ),
  updateDimensionalGroupData: compose.action((state, payload: { rowIndex: number; displayName: string }) => {
    const existingDimensionalGroupData = state.get('dimensionalGroupRowData', []);
    return state
      .set(
        'dimensionalGroupRowData',
        existingDimensionalGroupData.map((row, index) => {
          if (index === payload.rowIndex) {
            return { ...row, name: [payload.displayName] };
          }
          return row;
        })
      )
      .set('agGridCacheBuster', state.get('agGridCacheBuster', AG_GRID_CACHE_BUSTER) + 1);
  }),
  updateAgGridCache: compose.payloadlessAction((state) => {
    return state.set('agGridCacheBuster', state.get('agGridCacheBuster', AG_GRID_CACHE_BUSTER) + 1);
  }),
  setDimensionalReportConfiguration: compose.action((state, payload) =>
    state.set('dimensionalReportConfiguration', payload)
  ),
  setSelectedColumnEditorLensId: compose.action((state, id: string) => state.set('selectedColumnEditorLensId', id)),

  setEditModeDirty: compose.action((state, dirtyState: boolean) => state.set('isEditModeDirty', dirtyState)),

  setActiveNavigationPanelFilter: compose.action((state, payload: enumActiveNavigationPanelFilter) =>
    state.set('activeNavigationPanelFilter', payload)
  ),

  setNewRowEditMode: compose.action((state, payload: EGroupType) =>
    state
      .set('selectedColumnGroupIndex', null)
      .set('selectedRowGroupIndex', null)
      .set('editMode', EEditMode.RowNew)
      .set('newRowGroupType', payload)
      .set('editorDrawerOpen', true)
  ),

  setEditRowEditMode: compose.action((state, rowIndex: number) =>
    state
      .set('selectedRowGroupIndex', rowIndex)
      .set('newColumnGroupType', null)
      .set('newRowGroupType', null)
      .set('selectedColumnGroupIndex', null)
      .set('editMode', EEditMode.RowEdit)
      .set('editorDrawerOpen', true)
  ),

  setEditColEditMode: compose.action((state, colIndex: number) =>
    state
      .set('selectedRowGroupIndex', null)
      .set('newRowGroupType', null)
      .set('newColumnGroupType', null)
      .set('selectedColumnGroupIndex', colIndex)
      .set('editMode', EEditMode.ColEdit)
      .set('editorDrawerOpen', true)
      .set('activeNavigationPanelFilter', enumActiveNavigationPanelFilter.ROOT)
  ),

  setSegmentColEditMode: compose.action(
    (
      state,
      {
        columnIndex,
        segment,
        columnPath,
        level,
        source
      }: {
        columnIndex: number;
        segment: ColumnValueSegment[];
        columnPath?: ColumnValueSegment[];
        level?: number;
        source: IColumnDataSource | MultiHierarchyColumnDataSource;
      }
    ) =>
      state
        .set('selectedRowGroupIndex', null)
        .set('newRowGroupType', null)
        .set('newColumnGroupType', null)
        .set('selectedColumnGroupIndex', columnIndex)
        .set('selectedSegment', segment)
        // Absent when the click came from a caller that only knows the tuple; the editor then falls
        // back to treating the selection as level 1, which is what it always did.
        .set('selectedColumnPath', columnPath ?? null)
        .set('selectedColumnLevel', level ?? null)
        .set('selectedSource', source)
        .set('editMode', EEditMode.SubColEdit)
        .set('editorDrawerOpen', true)
        .set('activeNavigationPanelFilter', enumActiveNavigationPanelFilter.ROOT)
  ),

  toggleEditorDrawer: compose.payloadlessAction((state) =>
    state.set('editorDrawerOpen', !state.get('editorDrawerOpen'))
  ),

  updateAgGridColumnHeader: compose.action((state, payload: { columnGroupIndex: number; newName: string }) => {
    const columns = state.get('agGridColumns');
    if (!columns || columns[payload.columnGroupIndex] === undefined) return state;
    const updated = columns.map((col: ColDef, i: number) =>
      i === payload.columnGroupIndex ? { ...col, headerName: payload.newName } : col
    );
    return state.set('agGridColumns', updated);
  }),

  updateAgGridChildColumnHeader: compose.action(
    (state, payload: { columnGroupIndex: number; columnValue: string; newName: string; fallbackName?: string }) => {
      const columns = state.get('agGridColumns');
      if (!columns || columns[payload.columnGroupIndex] === undefined) return state;
      const parent = columns[payload.columnGroupIndex];
      if (!parent?.children) return state;
      const effectiveName = (payload.newName || payload.fallbackName) ?? payload.columnValue;
      const updatedChildren = parent.children.map((child: ColDef) => {
        const fieldMatchesValue = typeof child.field === 'string' && child.field.includes(`.${payload.columnValue}.`);
        if (!fieldMatchesValue) return child;
        return { ...child, headerName: effectiveName, headerValueGetter: () => effectiveName };
      });
      const updatedColumns = columns.map((col: ColDef, i: number) =>
        i === payload.columnGroupIndex ? { ...col, children: updatedChildren } : col
      );
      return state.set('agGridColumns', updatedColumns);
    }
  )
}));

export const {
  actions: reportExplorerActions,
  actionCreators: reportExplorerActionCreators,
  reducer: reportExplorerReducer,
  selector: reportExplorerSelector
} = reportExplorerState;
